// =====================================================================
// Aba 3 — Protocolos (Fase 3, S38).
// CRUD clinic_protocols. Campos required: protocol_key (slug),
// name, protocol_type (particular|convenio), total_steps, total_duration_min.
// =====================================================================
import React from "react";
import { Chip } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import CrudTable from "../components/CrudTable";
import CrudDialog from "../components/CrudDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import useCrudResource from "../hooks/useCrudResource";

import {
  listProtocols,
  createProtocol,
  updateProtocol,
  deleteProtocol
} from "../../../services/clinicAdminService";

const TYPE_LABEL = {
  particular: "Particular",
  convenio: "Convênio"
};

const TYPE_COLOR = {
  particular: "#7c3aed",
  convenio: "#03a9f4"
};

const COLUMNS = [
  { key: "name", label: "Nome" },
  {
    key: "protocol_key",
    label: "Chave",
    width: 200,
    render: (r) => <code>{r.protocol_key}</code>
  },
  {
    key: "protocol_type",
    label: "Tipo",
    width: 110,
    render: (r) => (
      <Chip
        size="small"
        label={TYPE_LABEL[r.protocol_type] || r.protocol_type}
        style={{
          background: `${TYPE_COLOR[r.protocol_type] || "#999"}22`,
          color: TYPE_COLOR[r.protocol_type] || "#999",
          fontWeight: 600
        }}
      />
    )
  },
  { key: "total_steps", label: "Etapas", width: 80, align: "right" },
  {
    key: "total_duration_min",
    label: "Duração (min)",
    width: 110,
    align: "right"
  },
  {
    key: "is_active",
    label: "Ativo",
    width: 70,
    align: "center",
    render: (r) =>
      r.is_active ? (
        <CheckCircleIcon fontSize="small" htmlColor="#06c270" />
      ) : (
        "—"
      )
  }
];

const FIELDS = [
  {
    key: "protocol_key",
    label: "Chave (slug)",
    type: "text",
    required: true,
    colSpan: 6,
    regex: /^[a-z0-9_-]+$/i,
    regexError: "Apenas letras, números, _ e -",
    placeholder: "rinoplastia, follow_up_30d",
    helperText: "Identificador único (max 64). Imutável após criação."
  },
  {
    key: "name",
    label: "Nome",
    type: "text",
    required: true,
    colSpan: 6
  },
  { key: "description", label: "Descrição", type: "textarea", rows: 2 },
  {
    key: "protocol_type",
    label: "Tipo",
    type: "select",
    required: true,
    colSpan: 4,
    options: [
      { value: "particular", label: "Particular" },
      { value: "convenio", label: "Convênio" }
    ]
  },
  {
    key: "total_steps",
    label: "Etapas",
    type: "number",
    required: true,
    colSpan: 4,
    transform: (v) => Number(v),
    helperText: "Quantas consultas/sessões"
  },
  {
    key: "total_duration_min",
    label: "Duração total (min)",
    type: "number",
    required: true,
    colSpan: 4,
    transform: (v) => Number(v)
  },
  {
    key: "buffer_minutes",
    label: "Buffer entre etapas (min)",
    type: "number",
    colSpan: 6,
    transform: (v) => (v === "" ? null : Number(v))
  },
  { key: "is_active", label: "Ativo", type: "switch", colSpan: 6 },
  {
    key: "price",
    label: "Preço (R$)",
    type: "number",
    colSpan: 4,
    step: 0.01,
    transform: (v) => (v === "" ? null : Number(v))
  },
  {
    key: "price_text",
    label: "Texto do preço",
    type: "text",
    colSpan: 8,
    placeholder: "R$ 5.000,00 (parcelado em 10x)"
  },
  { key: "notes", label: "Notas internas", type: "textarea", rows: 2 }
];

const ProtocolosTab = ({ slug }) => {
  const crud = useCrudResource({
    slug,
    list: listProtocols,
    create: createProtocol,
    update: updateProtocol,
    remove: deleteProtocol,
    resourceLabel: "protocolo"
  });

  return (
    <>
      <CrudTable
        columns={COLUMNS}
        rows={crud.rows}
        loading={crud.loading}
        onAdd={crud.handleAdd}
        onEdit={crud.handleEdit}
        onDelete={crud.handleDelete}
        searchPlaceholder="Buscar protocolo…"
        searchFields={["name", "protocol_key"]}
      />

      <CrudDialog
        open={crud.dialogOpen}
        onClose={crud.handleCloseDialog}
        onSave={crud.handleSave}
        title={crud.dialogInitial ? "Editar protocolo" : "Novo protocolo"}
        fields={FIELDS}
        initialValues={crud.dialogInitial}
        saving={crud.saving}
      />

      <ConfirmDeleteDialog
        open={!!crud.deleteTarget}
        onClose={crud.handleCancelDelete}
        onConfirm={crud.handleConfirmDelete}
        itemLabel={crud.deleteTarget?.name}
        saving={crud.saving}
      />
    </>
  );
};

export default ProtocolosTab;
